import { CalendarIcon, MapPinIcon, ClockIcon, PlusIcon } from '@heroicons/react/24/outline';
import { openCalendarInvite } from '../utils/calendarInvite.js';
import { saveEventToUser } from '../utils/eventActions.js';
import { userSession } from '../utils/userSession.js';
import { trackEvent, EventTypes } from '../utils/analytics.js';
import { 
  parseISODate, 
  formatTime, 
  monthNames 
} from '../utils/dateUtils.js';
import { useState } from 'react';
import StatusOverlay from './StatusOverlay.js';

export default function EventList({ event, onOpenLogin, onEventAdded }) {
  const [statusMessage, setStatusMessage] = useState('');
  const [showStatus, setShowStatus] = useState(false);
  const [statusType, setStatusType] = useState('success');

  const showStatusMessage = (message, type = 'success') => {
    setStatusMessage(message);
    setStatusType(type);
    setShowStatus(true);
    setTimeout(() => {
      setShowStatus(false);
      setStatusMessage('');
      setStatusType('success');
    }, 2000);
  };

  const handleAddEvent = async (eventId) => {
    if (!userSession.isLoggedIn()) {
      onOpenLogin("Sign in to add events to DriftCal");
      return;
    }

    try {
      const result = await saveEventToUser(null, eventId);
      showStatusMessage(
        result.alreadySaved ? 'Event already saved' : 'Event added',
        result.alreadySaved ? 'warning' : 'success'
      );
      if (!result.alreadySaved) {
        onEventAdded();
      }
    } catch (error) {
      console.error('Error in handleAddEvent:', error);
      showStatusMessage('Failed to add event', 'error');
    }
  };

  const handleCalendarInvite = (event) => {
    trackEvent(EventTypes.ICAL_DOWNLOAD, {
      event_id: event.EventID,
      event_name: event.EventName,
      promoter_id: event.OrganiserID,
      promoter_name: event.BusinessName,
      user_id: userSession.getUserId() || 'anonymous'
    });
    openCalendarInvite(event);
  };

  const handleEventNameClick = (event) => {
    trackEvent(EventTypes.EVENT_CLICK, {
      event_id: event.EventID,
      event_name: event.EventName,
      promoter_id: event.OrganiserID,
      promoter_name: event.BusinessName,
      view_type: 'list',
      click_target: 'title',
      user_id: userSession.getUserId() || 'anonymous'
    });
  };

  const getEventTypeStyles = (eventType) => {
    switch(eventType) {
      case 'Beginner':
        return 'bg-blue-300 text-blue-800';
      case 'Practice':
        return 'bg-green-200 text-green-800';
      case 'Competition':
        return 'bg-orange-200 text-orange-800';
      case 'Exhibition':
        return 'bg-red-200 text-red-800';
      default:
        return 'bg-gray-200 text-gray-800';
    }
  };

  const dateInfo = parseISODate(event.StartDateTime);

  const getStateStyles = (stateCode) => {
    switch(stateCode) {
      case 'VIC':
        return 'bg-gray-500';
      case 'NSW':
        return 'bg-sky-500';
      case 'ACT':
        return 'bg-blue-500';
      case 'QLD':
        return 'bg-rose-800';
      case 'SA':
        return 'bg-red-600';
      case 'NT':
        return 'bg-orange-700';
      case 'WA':
        return 'bg-yellow-500';
      case 'TAS':
        return 'bg-green-600';
      default:
        return 'bg-slate-900';
    }
  };

  return (
    <div className="relative">
      {showStatus && (
        <StatusOverlay 
          message={statusMessage} 
          type={statusType}
        />
      )}
      
      <div className="bg-white rounded-lg shadow p-4 flex items-start">
        {/* Action Buttons - Moved to left */}
        <div className="flex flex-col gap-2 mr-4">
          <div className="flex flex-col items-center">
            <button 
              className="inline-flex items-center justify-center rounded-md p-0 text-slate-900 hover:text-gray-800 h-8 w-8"
              onClick={() => handleAddEvent(event.EventID)}
              title="Add to DriftCal"
            >
              <PlusIcon className="h-6 w-6 sm:h-6 sm:w-6" />
            </button>
            <span className="text-[8px] sm:text-[10px] text-slate-600 whitespace-nowrap">Add to DriftCal</span>
          </div>
          
          <div className="flex flex-col items-center">
            <button 
              className="inline-flex items-center justify-center rounded-md p-0 text-slate-900 hover:text-gray-800 h-8 w-8"
              onClick={() => handleCalendarInvite(event)}
              title="Download iCal"
            >
              <CalendarIcon className="h-6 w-6 sm:h-6 sm:w-6" />
            </button>
            <span className="text-[8px] sm:text-[10px] text-slate-600 whitespace-nowrap">Download iCal</span>
          </div>
        </div>

        {/* Date Display with State */}
        <div className="flex flex-col gap-2 mr-4">
          <div className="flex flex-col items-center justify-center bg-slate-900 text-white rounded p-2 w-16">
            <span className="text-2xl font-bold">{dateInfo.day}</span>
            <span className="text-sm font-medium">
              {monthNames[dateInfo.month - 1].substring(0, 3).toUpperCase()}
            </span>
          </div>
          <span className={`text-xs px-2 py-1 ${getStateStyles(event.StateNameShort)} text-white rounded-full text-center`}>
            {event.StateNameShort}
          </span>
        </div>

        {/* Event Details */}
        <div className="flex-grow overflow-hidden">
          <div className="flex items-center gap-2 mb-1">
            <h4 className="font-semibold text-slate-900">
              <a 
                href={event.EventLink} 
                target="_blank" 
                rel="noopener noreferrer"
                onClick={() => handleEventNameClick(event)}
                className="hover:underline text-[12px] sm:text-base truncate block"
              >
                {event.EventType === 'Competition' && event.RoundNum
                  ? `${event.EventName} - Round ${event.RoundNum}`
                  : event.EventName}
              </a>
            </h4>
          </div>
          
          <div className="flex items-center gap-2">
            <p className="text-[10px] sm:text-xs text-slate-500 truncate">
              {event.BusinessName}
            </p>
            <span className={`text-[10px] sm:text-xs px-2 py-0.5 ${getEventTypeStyles(event.EventType)} rounded-full whitespace-nowrap`}>
              {event.EventType}
            </span>
          </div>

          <div className="mt-2 space-y-1">
            <div className="flex items-center text-[10px] sm:text-sm text-slate-600">
              <ClockIcon className="h-4 w-4 mr-1.5 flex-shrink-0 text-slate-400" />
              <span className="truncate">
                {formatTime(event.StartDateTime)} - {formatTime(event.EndDateTime)}
              </span>
            </div>

            <div className="flex items-center text-[10px] sm:text-sm text-slate-600">
              <MapPinIcon className="h-4 w-4 mr-1.5 flex-shrink-0 text-slate-400" />
              <div className="min-w-0 flex-1 overflow-hidden">
                <a 
                  href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(event.VenueName)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-blue-600 truncate block w-full"
                >
                  {event.VenueName}
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
} 